import { apiClient } from './apiService'

export interface TimelineData {
  startedAtSprint: string
  endsAtSprint: string
  startDate: string | null
  endDate: string | null
  hasCompleteTimeline: boolean
  error?: string
}

export interface TimelineResponse {
  status: string
  message: string
  data: Record<string, TimelineData>
  total: number
  withTimeline: number
  timestamp: string
}

export interface SingleTimelineResponse {
  status: string
  message: string
  data: TimelineData
  timestamp: string
}

class TimelineService {
  /**
   * Get timeline data for multiple initiatives
   */
  async getInitiativesTimeline(keys: string[]): Promise<TimelineResponse> {
    console.log(`🕒 Frontend Timeline: Fetching timeline for ${keys.length} initiatives`)

    const response = await apiClient.post<TimelineResponse>('/timeline/initiatives', {
      keys
    })

    console.log(`🕒 Frontend Timeline: Received timeline data for ${Object.keys(response.data.data).length} initiatives`)
    console.log(`🕒 Frontend Timeline: ${response.data.withTimeline} initiatives have complete timeline data`)

    return response.data
  }

  /**
   * Get timeline data for a single initiative
   */
  async getInitiativeTimeline(key: string): Promise<SingleTimelineResponse> {
    console.log(`🕒 Frontend Timeline: Fetching timeline for ${key}`)

    const response = await apiClient.get<SingleTimelineResponse>(`/timeline/initiative/${key}`)

    console.log(`🕒 Frontend Timeline: Received timeline data for ${key}:`, response.data.data)

    return response.data
  }

  /**
   * Merge timeline data with initiatives data
   */
  mergeTimelineWithInitiatives<T extends { key: string }>(
    initiatives: T[],
    timelineData: Record<string, TimelineData>
  ): (T & { timeline: TimelineData })[] {
    console.log(`🕒 Frontend Timeline: Merging timeline data with ${initiatives.length} initiatives`)
    
    return initiatives.map(initiative => ({
      ...initiative,
      timeline: timelineData[initiative.key] || {
        startedAtSprint: '',
        endsAtSprint: '',
        startDate: null,
        endDate: null,
        hasCompleteTimeline: false
      }
    }))
  }

  /**
   * Extract initiative keys from initiatives data
   */
  extractKeys<T extends { key: string }>(initiatives: T[]): string[] {
    return initiatives.map(initiative => initiative.key)
  }

  /**
   * Filter initiatives that have complete timeline data
   */
  filterWithTimeline<T extends { timeline: TimelineData }>(initiatives: T[]): T[] {
    return initiatives.filter(initiative => initiative.timeline.hasCompleteTimeline)
  }

  /**
   * Get timeline statistics
   */
  getTimelineStats<T extends { timeline: TimelineData }>(initiatives: T[]): {
    total: number
    withTimeline: number
    withoutTimeline: number
    percentage: number
  } {
    const total = initiatives.length
    const withTimeline = initiatives.filter(i => i.timeline.hasCompleteTimeline).length
    const withoutTimeline = total - withTimeline
    const percentage = total > 0 ? Math.round((withTimeline / total) * 100) : 0

    return {
      total,
      withTimeline,
      withoutTimeline,
      percentage
    }
  }
}

export const timelineService = new TimelineService()
export default timelineService
